import { useEffect, useRef } from 'react';
import { trackContact } from '../tracking';

/**
 * Loads the GoHighLevel (LeadConnector) chat widget.
 *
 * IMPORTANT: React does NOT execute <script> tags rendered through JSX — the
 * browser inserts them into the DOM but never runs them, so a
 * `return <script ... />` approach mounts the tag yet the widget never loads.
 *
 * We instead inject the script imperatively via the DOM after mount, which is
 * exactly what pasting the snippet into the page <body> does. A guard keeps it
 * from loading twice (React StrictMode double-invokes effects in dev).
 *
 * Opening this widget is the CONVERSION event of the page, so we fire the Meta
 * standard `Contact` event the first time the visitor opens it. The widget lives
 * inside a custom element with its own shadow root, so a normal click listener
 * on the element would never see it — we read the event's composedPath(), which
 * does cross shadow boundaries.
 */
const WIDGET_ID = '6a77906db0aa0f9282b17021';
const LOADER_SRC = 'https://widgets.leadconnectorhq.com/loader.js';
function isWidgetNode(node: EventTarget): boolean {
  const el = node as Element;
  if (!el || typeof el.tagName !== 'string') return false;
  const tag = el.tagName.toLowerCase();
  if (tag.includes('chat-widget') || tag.includes('lead-connector')) return true;
  const id = typeof el.id === 'string' ? el.id.toLowerCase() : '';
  return id.includes('lc_') || id.includes('chat-widget');
}
export function GHLWidget() {
  const contactFired = useRef(false);
  useEffect(() => {
    if (!document.querySelector(`script[data-widget-id="${WIDGET_ID}"]`)) {
      const script = document.createElement('script');
      script.src = LOADER_SRC;
      script.async = true;
      script.setAttribute('data-resources-url', 'https://widgets.leadconnectorhq.com/chat-widget/loader.js');
      script.setAttribute('data-widget-id', WIDGET_ID);
      document.body.appendChild(script);
    }
    const fire = (context: string) => {
      if (contactFired.current) return;
      contactFired.current = true;
      trackContact(context);
    };

    // Any click that passes through the widget's tree counts as opening it.
    const onPointerDown = (event: Event) => {
      const path = (event as PointerEvent).composedPath?.() ?? [];
      if (path.some(isWidgetNode)) fire('ghl_chat_widget');
    };

    // The widget also posts messages to the host page as it opens.
    const onMessage = (event: MessageEvent) => {
      if (typeof event.origin === 'string' && event.origin.includes('leadconnectorhq.com')) {
        fire('ghl_chat_widget');
      }
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('message', onMessage);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('message', onMessage);
    };
  }, []);
  return null;
}